import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // Try cookie-based auth via /api/auth/me (httpOnly cookie sent automatically)
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setLoading(false);
        return;
      }
    } catch (err) {
      logger.error('Auth check failed:', err);
    }

    // Fallback: check localStorage for legacy local-login users only
    const storedUser = localStorage.getItem('milconnect_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (parseErr) {
        logger.error('Failed to parse stored user:', parseErr);
        localStorage.removeItem('milconnect_user');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // If user data was passed via navigation state (from AuthCallback), use it directly
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }

    checkAuth();
  }, [checkAuth, location.state]);

  const login = (userData) => {
    setUser(userData);
    // Store for legacy local-login flow display only (not tokens)
    localStorage.setItem('milconnect_user', JSON.stringify(userData));
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (logoutErr) {
      logger.error('Logout request failed:', logoutErr);
    }
    setUser(null);
    localStorage.removeItem('milconnect_user');
    navigate('/');
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    localStorage.setItem('milconnect_user', JSON.stringify(newUserData));
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
